/** Copy, then say so for a second and a half.
 *
 * This exists for one moment in the product: the connection string is shown
 * once, and if the copy silently fails the user has lost a token. So the
 * button falls back to a selection-and-`execCommand` copy when the clipboard
 * API is unavailable (it is, on an insecure origin — which is exactly the
 * LAN self-hosting case the connection string is designed for), and it says
 * plainly when it could not copy rather than pretending it did.
 *
 * The confirmation is announced as well as shown: a button whose label
 * changes under a pointer is invisible to anyone who pressed it with a
 * keyboard and is listening.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface CopyButtonProps {
  /** What lands on the clipboard. */
  text: string;
  label?: string;
  copiedLabel?: string;
  failedLabel?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
  /** Names the button when the visible label is just "Copy". */
  title?: string;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Denied, or not a secure context. Fall through to the old way.
  }

  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}

export function CopyButton({
  text,
  label = 'Copy',
  copiedLabel = 'Copied',
  failedLabel = 'Press ⌘C',
  variant = 'primary',
  className,
  title,
}: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const onClick = useCallback(() => {
    void copyToClipboard(text).then((ok) => {
      setState(ok ? 'copied' : 'failed');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setState('idle'), 1500);
    });
  }, [text]);

  const shown = state === 'copied' ? copiedLabel : state === 'failed' ? failedLabel : label;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        title={title}
        className={['btn', `btn-${variant}`, className ?? ''].filter(Boolean).join(' ')}
      >
        {shown}
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        {state === 'copied' ? 'copied to the clipboard' : ''}
      </span>
    </>
  );
}

export default CopyButton;
